import React, { useEffect, useId, useRef, useState } from 'react';
import { useField } from 'formik';
import { BaseOptions, PhotoOptions } from '@reviews/interfaces';
import { useCreateGuestContentUploadUrlsMutation } from '../../services';
import { compressImageToJpeg } from './compressImage';
import { MessageUtils } from '../../assests/strings/messages';
import './PhotoUpload.scss';

export type PriorPhoto = {
    review_content_id: number;
    s3_key: string;
    description?: string | null;
    read_url?: string | null;
};

export type PhotoAnswerItem = {
    s3_key: string;
};

type LocalPhoto = {
    id: string;
    s3_key: string;
    previewUrl: string;
    uploading?: boolean;
    error?: string;
};

interface PhotoUploadProps {
    name: string;
    title: string | null;
    description: string | null;
    required: boolean;
    configuration: BaseOptions & PhotoOptions;
    adminReservationId: number;
    nsu: string;
    confirmation?: string;
    /** Read-only prior photos (PAL review after during-stay). */
    priorContent?: PriorPhoto[];
    /** Editable photos seeded on resume of during-stay content. */
    initialPhotos?: PriorPhoto[];
}

const DEFAULT_ACCEPT = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
const DEFAULT_MAX_PHOTOS = 10;

const toLocalPhotos = (photos: PriorPhoto[]): LocalPhoto[] =>
    photos
        .filter((photo) => Boolean(photo.s3_key))
        .map((photo) => ({
            id: `prior-${photo.review_content_id || photo.s3_key}`,
            s3_key: photo.s3_key,
            previewUrl: photo.read_url || '',
        }));

export function PhotoUpload(props: PhotoUploadProps) {
    const {
        name,
        title,
        description,
        required,
        configuration,
        adminReservationId,
        nsu,
        confirmation,
        priorContent = [],
        initialPhotos = [],
    } = props;
    const fieldName = `${name}-answer`;
    const [, , helpers] = useField<PhotoAnswerItem[]>(fieldName);
    const inputId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [photos, setPhotos] = useState<LocalPhoto[]>(() => toLocalPhotos(initialPhotos));
    const [createUploadUrls] = useCreateGuestContentUploadUrlsMutation();

    const options = configuration.options || {};
    const maxPhotos = options.max_photos ?? DEFAULT_MAX_PHOTOS;
    const accept = (options.accept || DEFAULT_ACCEPT).join(',');
    const placeholder = options.placeholder || title || 'Tap to add photos';
    const helperText = options.helper_text || description || 'Take a photo or choose from your library';
    const remainingSlots = Math.max(0, maxPhotos - photos.length);
    const canAddMore = remainingSlots > 0;

    useEffect(() => {
        helpers.setValue(
            photos.filter((photo) => photo.s3_key && !photo.uploading && !photo.error).map((photo) => ({ s3_key: photo.s3_key })),
            true
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [photos]);

    const openPicker = () => {
        if (!canAddMore) {
            return;
        }
        inputRef.current?.click();
    };

    const removePhoto = (event: React.MouseEvent, id: string) => {
        event.preventDefault();
        event.stopPropagation();
        setPhotos((current) => {
            const target = current.find((photo) => photo.id === id);
            if (target?.previewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(target.previewUrl);
            }
            return current.filter((photo) => photo.id !== id);
        });
    };

    const markPhotoError = (pendingId: string, error = 'Upload failed') => {
        setPhotos((current) => current.map((photo) => (photo.id === pendingId ? { ...photo, uploading: false, error } : photo)));
    };

    const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []).slice(0, remainingSlots);
        event.target.value = '';
        if (files.length === 0) {
            return;
        }

        const pending: LocalPhoto[] = files.map((file, index) => ({
            id: `${Date.now()}-${index}`,
            s3_key: '',
            previewUrl: URL.createObjectURL(file),
            uploading: true,
        }));
        setPhotos((current) => [...current, ...pending]);

        // Compress on-device first (hard ≤2_000_000 bytes JPEG) so Vacasa AI validation accepts the upload.
        const prepared = await Promise.all(
            files.map(async (file, index) => {
                const pendingId = pending[index].id;
                try {
                    const compressed = await compressImageToJpeg(file);
                    const previewUrl = URL.createObjectURL(compressed);
                    setPhotos((current) =>
                        current.map((photo) => {
                            if (photo.id !== pendingId) {
                                return photo;
                            }
                            if (photo.previewUrl?.startsWith('blob:')) {
                                URL.revokeObjectURL(photo.previewUrl);
                            }
                            return { ...photo, previewUrl };
                        })
                    );
                    return { pendingId, file: compressed };
                } catch {
                    markPhotoError(pendingId, 'Could not process photo');
                    return null;
                }
            })
        );

        const ready = prepared.filter((item): item is { pendingId: string; file: File } => Boolean(item));
        if (ready.length === 0) {
            return;
        }

        try {
            const response = await createUploadUrls({
                admin_reservation_id: adminReservationId,
                nsu,
                ...(confirmation ? { confirmation } : {}),
                count: ready.length,
                mime_types: ready.map(() => 'image/jpeg'),
            }).unwrap();

            // JSON:API shape is data.attributes.uploads; keep fallbacks for serializer quirks.
            const uploads =
                response?.data?.attributes?.uploads || (response as any)?.attributes?.uploads || (response as any)?.uploads || [];

            if (!Array.isArray(uploads) || uploads.length === 0) {
                // eslint-disable-next-line no-console
                console.error('Guest content upload URLs missing in response', response);
                ready.forEach(({ pendingId }) => markPhotoError(pendingId));
                return;
            }

            await Promise.all(
                ready.map(async ({ pendingId, file }, index) => {
                    const upload = uploads[index];
                    if (!upload?.upload_url || !upload?.s3_key) {
                        // eslint-disable-next-line no-console
                        console.error('Missing upload_url/s3_key for photo index', index, upload);
                        markPhotoError(pendingId);
                        return;
                    }

                    try {
                        const putResponse = await fetch(upload.upload_url, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'image/jpeg' },
                            body: file,
                        });

                        if (!putResponse.ok) {
                            // eslint-disable-next-line no-console
                            console.error('S3 PUT failed', putResponse.status, await putResponse.text().catch(() => ''));
                            markPhotoError(pendingId);
                            return;
                        }

                        setPhotos((current) =>
                            current.map((photo) =>
                                photo.id === pendingId ? { ...photo, s3_key: upload.s3_key, uploading: false, error: undefined } : photo
                            )
                        );
                    } catch (error) {
                        // Typical when S3 bucket CORS is missing: TypeError Failed to fetch (no API/Lambda logs).
                        // eslint-disable-next-line no-console
                        console.error('S3 PUT network/CORS error', error);
                        markPhotoError(pendingId);
                    }
                })
            );
        } catch {
            setPhotos((current) =>
                current.map((photo) =>
                    ready.some((item) => item.pendingId === photo.id) ? { ...photo, uploading: false, error: 'Upload failed' } : photo
                )
            );
        }
    };

    const totalVisible = priorContent.length + photos.length;
    const isReviewWithPrior = Boolean(options.references_previous_content);
    // During-stay copy lives inside the dropzone; only show an external heading when reviewing prior content.
    const showHeading = isReviewWithPrior && Boolean(title || description || priorContent.length);

    return (
        <div className={`question photo-upload ${isReviewWithPrior ? 'photo-upload--with-prior' : ''}`}>
            {showHeading && (
                <div className="field-wrapper base-font-body category-name photo-upload__heading">
                    {title || 'Your photos'}
                    {description && <div className="caption category-description">{description}</div>}
                </div>
            )}

            {priorContent.length > 0 && (
                <div className="photo-upload__prior">
                    <div className="photo-upload__list-label">Already shared ({priorContent.length})</div>
                    <div className="photo-upload__grid photo-upload__grid--scroll">
                        {priorContent.map((photo) => (
                            <div key={photo.review_content_id} className="photo-upload__thumb photo-upload__thumb--prior">
                                <img src={photo.read_url || ''} alt={photo.description || 'Uploaded photo'} />
                                {photo.description && <div className="photo-upload__thumb-label">{photo.description}</div>}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <button
                type="button"
                className={`photo-upload__dropzone ${!canAddMore ? 'photo-upload__dropzone--disabled' : ''}`}
                onClick={openPicker}
                disabled={!canAddMore}
                aria-label={placeholder}
            >
                <span className="photo-upload__camera-icon" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M9 3L7.17 5H4C2.9 5 2 5.9 2 7V19C2 20.1 2.9 21 4 21H20C21.1 21 22 20.1 22 19V7C22 5.9 21.1 5 20 5H16.83L15 3H9ZM12 18C9.24 18 7 15.76 7 13C7 10.24 9.24 8 12 8C14.76 8 17 10.24 17 13C17 15.76 14.76 18 12 18ZM12 10C10.34 10 9 11.34 9 13C9 14.66 10.34 16 12 16C13.66 16 15 14.66 15 13C15 11.34 13.66 10 12 10Z"
                            fill="currentColor"
                        />
                    </svg>
                </span>
                <span className="photo-upload__dropzone-title">{canAddMore ? placeholder : 'Photo limit reached'}</span>
                {canAddMore && <span className="photo-upload__dropzone-helper">{helperText}</span>}
            </button>

            <div className="photo-upload__consent">
                <span className="photo-upload__consent-icon" aria-hidden="true">
                    ⓘ
                </span>
                {MessageUtils['description_photo_consent']}
            </div>

            <input ref={inputRef} id={inputId} type="file" accept={accept} multiple hidden onChange={handleFilesSelected} />

            {photos.length > 0 && (
                <div className="photo-upload__new">
                    <div className="photo-upload__list-label">
                        {priorContent.length > 0 ? 'New photos' : 'Your photos'} ({photos.length}){' — tap × to remove'}
                    </div>
                    <div className="photo-upload__grid">
                        {photos.map((photo) => (
                            <div
                                key={photo.id}
                                className={`photo-upload__thumb ${photo.uploading ? 'photo-upload__thumb--uploading' : ''} ${
                                    photo.error ? 'photo-upload__thumb--error' : ''
                                }`}
                            >
                                <img src={photo.previewUrl} alt="Selected photo" />
                                {photo.uploading && <div className="photo-upload__status">Uploading…</div>}
                                {photo.error && <div className="photo-upload__status">{photo.error}</div>}
                                <button
                                    type="button"
                                    className="photo-upload__remove"
                                    onClick={(event) => removePhoto(event, photo.id)}
                                    aria-label="Remove photo"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {required && isReviewWithPrior && totalVisible === 0 && <label className="required-label">Required</label>}
        </div>
    );
}
